/**
 * Client-side FEATURE_GATE error handling.
 *
 * The server's feature-gate middleware (packages/api/src/middleware/
 * feature-gate.ts) throws ORPCError('FORBIDDEN') with a structured payload
 * on `error.data` when a plan-based gate fires. The QueryClient's
 * MutationCache (see router.tsx) auto-redirects users to
 * `/pricing?limit=<feature>` on any failed *mutation*. But direct
 * `client.ai.*` calls (deal extraction / valuation) bypass React Query
 * entirely, so we expose the same handler here for those sites to use.
 */

import { isFeatureName } from '@app/billing-config';

/**
 * True when the error is a server-side FEATURE_GATE response. Use this in
 * loaders/queries that should DEGRADE gracefully (e.g. brand detail page
 * still renders for free users; the gated match-score section just shows
 * an upsell instead of crashing the route) rather than redirecting the
 * whole page to /pricing.
 */
export function isFeatureGateError(error: unknown): boolean {
  return getFeatureGateFeature(error) !== null;
}

export function getFeatureGateFeature(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const payload = data as { kind?: unknown; feature?: unknown };
  if (payload.kind !== 'FEATURE_GATE') return null;
  if (typeof payload.feature !== 'string' || !isFeatureName(payload.feature)) return null;
  return payload.feature;
}

/**
 * Inspect a thrown error: if it's a FEATURE_GATE response, navigate the
 * browser to /pricing with the feature param. Returns `true` if a redirect
 * was issued (caller should typically return early instead of toasting).
 *
 * No-op on the server (SSR) where window is undefined; the caller's
 * regular error path will surface the message there.
 */
export function handleFeatureGateError(error: unknown): boolean {
  if (typeof window === 'undefined') return false;
  const feature = getFeatureGateFeature(error);
  if (!feature) return false;
  window.location.assign(`/pricing?limit=${encodeURIComponent(feature)}`);
  return true;
}
